import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { fetchClinicVisibility, putClinicVisibility } from '@/lib/api/settings';
import { hasPermission } from '@/lib/stores/permissions-store';
import { hiddenMapFromClinics, hiddenMapsEqual, toggleSurfaceHidden } from '@/lib/clinic-visibility';

// Editor for clinic visibility per UI surface (global settings, org-wide): a clinics × surfaces
// table where a ticked box means the clinic is SHOWN there. The stored value is the hidden
// complement, so a clinic opened later is visible everywhere by default. canEdit comes from the
// payload (the settings:manage gate, computed server-side) — seeded from the local permission
// while loading. An older backend has no endpoint (404) → read-only "not supported" note.
export function ClinicVisibilityEditor() {
  const [clinics, setClinics] = useState([]);
  const [surfaces, setSurfaces] = useState([]);
  const [hidden, setHidden] = useState({});
  const [baseline, setBaseline] = useState({});
  const [canEdit, setCanEdit] = useState(() => hasPermission('settings', 'manage'));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [unsupported, setUnsupported] = useState(false);

  const seed = (view) => {
    setClinics(view.clinics);
    setSurfaces(view.surfaces);
    const map = hiddenMapFromClinics(view.clinics);
    setHidden(map);
    setBaseline(map);
    setCanEdit(Boolean(view.canEdit));
  };

  useEffect(() => {
    let cancelled = false;
    fetchClinicVisibility()
      .then((view) => {
        if (cancelled) return;
        seed(view);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        if (error?.status === 404) {
          setUnsupported(true);
          setCanEdit(false);
          return;
        }
        setLoadFailed(true);
        setCanEdit(false);
        toast.error(error instanceof Error ? error.message : 'Could not load clinic visibility.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = !hiddenMapsEqual(hidden, baseline);
  const editable = canEdit && !unsupported && !loadFailed;

  const handleSave = async () => {
    if (!editable || !isDirty) return;
    setIsSaving(true);
    try {
      const view = await putClinicVisibility({ hidden });
      seed(view);
      toast.success('Clinic visibility saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save clinic visibility.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading clinics…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {unsupported ? (
        <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Clinic visibility is not supported by this server yet — every clinic shows on every surface until the
          backend is updated.
        </p>
      ) : null}
      {loadFailed ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          The clinic list could not be loaded. Reload before editing.
        </p>
      ) : null}

      {clinics.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="bg-muted/40 text-left text-[10.5px] font-black uppercase tracking-[0.08em] text-muted-foreground">
                <th className="border-b border-border px-3 py-2">Clinic</th>
                {surfaces.map((surface) => (
                  <th key={surface.key} className="border-b border-border px-3 py-2 text-center" title={`Shown on ${surface.label}`}>
                    {surface.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clinics.map((clinic, index) => {
                const hiddenHere = hidden[clinic.id] || [];
                const last = index === clinics.length - 1;
                const detail = [clinic.shortName, clinic.address].filter(Boolean).join(' · ');
                return (
                  <tr key={clinic.id} className="hover:bg-muted/30">
                    <td className={cn('px-3 py-2 align-middle', !last && 'border-b border-border')}>
                      <span className="block truncate text-[13px] font-semibold text-foreground">{clinic.name}</span>
                      {detail ? (
                        <span className="block truncate text-[11px] text-muted-foreground">{detail}</span>
                      ) : null}
                    </td>
                    {surfaces.map((surface) => {
                      const shown = !hiddenHere.includes(surface.key);
                      return (
                        <td
                          key={surface.key}
                          className={cn('px-3 py-2 text-center align-middle', !last && 'border-b border-border')}
                        >
                          <label className="inline-flex cursor-pointer items-center justify-center p-1">
                            <input
                              type="checkbox"
                              checked={shown}
                              disabled={!editable}
                              aria-label={`${clinic.name} shown on ${surface.label}`}
                              onChange={() => setHidden((current) => toggleSurfaceHidden(current, clinic.id, surface.key))}
                              className="size-4 accent-primary disabled:cursor-not-allowed"
                            />
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : unsupported || loadFailed ? null : (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
          No clinics to configure.
        </p>
      )}

      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex-1" />
          {isDirty ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="[&_svg]:size-3.5"
              onClick={() => setHidden(baseline)}
              disabled={isSaving}
            >
              <RotateCcw />
              Discard changes
            </Button>
          ) : null}
          <Button type="button" size="sm" onClick={handleSave} disabled={!isDirty || isSaving}>
            {isSaving ? 'Saving…' : 'Save visibility'}
          </Button>
        </div>
      ) : unsupported || loadFailed ? null : (
        <p className="text-xs text-muted-foreground">
          You need the settings <span className="font-semibold">manage</span> permission to edit these.
        </p>
      )}

      <div className="flex flex-col gap-1 text-[11.5px] leading-relaxed text-muted-foreground">
        <p>
          <span className="font-semibold text-foreground/80">Work Plan</span> — a clinic unticked here disappears from the
          Work Plan planner and the phone's team plan for everyone (its postings are kept and come back when it is
          ticked again). The planner's own "Clinics" control is a per-device layer on top of this.
        </p>
        <p>
          <span className="font-semibold text-foreground/80">Calendar views</span> — unticked clinics leave the clinic filters
          on the Calendar, Slots, Sessions and Time-off pages and the phone's Day / Sessions tabs. Booking forms and
          the other clinic pickers still list every clinic.
        </p>
      </div>
    </div>
  );
}
